import { flow, Instance, types } from 'mobx-state-tree'
import { addRoutingRule } from '../../network/addRoutingRule'
import { deleteRoutingRule } from '../../network/deleteRoutingRule'
import { fetchDynamicRouteConfig } from '../../network/fetchDynamicRouteConfig'
import { getAllPayerBankIds } from '../../network/getAllPayerBankIds'
import { getAllRoutingRules } from '../../network/getAllRoutingRules'
import { getAllRules } from '../../network/getAllRules'
import { getCardSchemesListApi } from '../../network/getCardSchemesList'
import { getEntityIdNameMap } from '../../network/getEntityIdNameMap'
import { getFileRoutingById } from '../../network/getFileRoutingById'
import { getPaymentsInstruments } from '../../network/getPaymentInstruments'
import { getRoutingById } from '../../network/getRoutingById'
import { routingProcessApprovals } from '../../network/routingProcessApprovals'
import { updateDynamicRouteConfig } from '../../network/updateDynamicRoutingConfig'
import { updateRoutingRule } from '../../network/updateRoutingRule'
import { updateRoutingStatus } from '../../network/updateRoutingStatus'
import { getErrorMessage, MessageTypes } from '../../utils'
import {
  CardSchema,
  CardTypes,
  FORM_MESSAGE,
  getIdNameValue,
  InstrumentTypes,
  TransactionType,
} from '../../utils/constants'
import { RootStore } from '../store'
import { Bank } from './Bank'
import { EntityIdName } from './EntityMaster'
import { OnboardCardItem } from './Onboarding'
import { Wallet } from './Wallet'

const Rule = types.model('Rule', {
  type: types.optional(types.literal('routing'), 'routing'),
  id: types.maybeNull(types.number),
  ruleName: types.optional(types.string, ''),
  payeeType: types.maybeNull(types.number),
  payeeUserId: types.optional(types.string, ''),
  payeeVpa: types.optional(types.string, ''),
  payerBankId: types.optional(types.string, ''),
  binList: types.optional(types.string, ''),
  binRange: types.optional(types.string, ''),
  cardType: types.optional(types.string, ''),
  scheme: types.optional(types.string, ''),
  amount: types.optional(types.string, ''),
  transactionInitiatingEntityId: types.optional(types.string, ''),
  transactionApplication: types.optional(types.string, ''),
  txntype: types.optional(types.string, ''),
  instrumentType: types.optional(types.string, ''),
  mode: types.maybeNull(types.number),
  activationGroup: types.optional(types.string, ''),
  priority: types.maybeNull(types.number),
  processor: types.optional(types.string, ''),
  isActive: types.optional(types.string, ''),
  isAssigned: types.optional(types.string, ''),
})

const DynamicConfig = types.model('DynamicConfig', {
  id: types.optional(types.string, ''),
  processingEntity: types.optional(types.string, ''),
  status: types.optional(types.string, ''),
  bankId: types.optional(types.string, ''),
  mode: types.optional(types.string, ''),
  subType: types.optional(types.string, ''),
  type: types.optional(types.string, ''),
  cardType: types.optional(types.string, ''),
  cardScheme: types.optional(types.string, ''),
  resetAfter: types.optional(types.string, ''),
  plannedDowntimeStartAt: types.maybeNull(types.Date),
  plannedDowntimeEndAt: types.maybeNull(types.Date),
  lastTxnDate: types.optional(types.string, ''),
  failureRate: types.optional(types.string, ''),
  editable: types.optional(types.boolean, false),
})

const RoutingRule = types
  .model('RoutingRule', {
    rule: types.optional(Rule, {}),
    rules: types.array(Rule),
    payerBankIds: types.array(types.string),
    dynamicConfig: types.optional(types.array(DynamicConfig), []),
    unsavedDynamicConfig: types.optional(types.array(DynamicConfig), []),
    bankList: types.array(Bank),
    walletList: types.array(Wallet),
    cardList: types.optional(types.array(OnboardCardItem), []),
    entityIdNames: types.array(EntityIdName),
  })
  .actions((self) => ({
    getById: flow(function* getById(id: number) {
      try {
        const response = yield getRoutingById(id, RootStore.session.getTokens)
        const own = self
        own.rule = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    undateEnableDynamicRouting: flow(function* undateEnableDynamicRouting(status: boolean) {
      try {
        const response = yield updateRoutingStatus(status, RootStore.session.getTokens)
        if (response?.recordStatus === 'SUCCESS') {
          RootStore.message.setMessage('Preferance saved Successfully', MessageTypes.SUCCESS)
        }
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    getAllRules: flow(function* getAllRules(search: string, filter: string) {
      try {
        const response = yield getAllRoutingRules(RootStore.session.getTokens, search, filter)
        const own = self
        own.rules = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    addRule: flow(function* addRule(data: any) {
      try {
        const response = yield addRoutingRule(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),

    deleteRule: flow(function* deleteRule(id) {
      try {
        const response = yield deleteRoutingRule(id, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),

    getRoutingFileById: flow(function* getRoutingFileById(id: number) {
      try {
        const response = yield getFileRoutingById(id, RootStore.session.getTokens)
        const own = self
        own.rule = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    processApprovals: flow(function* processApprovals(data: any) {
      try {
        const response = yield routingProcessApprovals(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),

    updateRule: flow(function* updateRule(data: any) {
      try {
        const response = yield updateRoutingRule(data, RootStore.session.getTokens)
        RootStore.message.setMessage(response?.message, MessageTypes.SUCCESS)
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR, FORM_MESSAGE)
        throw error
      }
    }),
    fetchPayerBankIds: flow(function* fetchPayerBankIds() {
      try {
        const response = yield getAllPayerBankIds(RootStore.session.getTokens)
        const own = self
        own.payerBankIds = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchAllRules: flow(function* fetchAllRules() {
      try {
        const response = yield getAllRules(RootStore.session.getTokens)
        const own = self
        own.rules = response
        return response
      } catch (error) {
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    fetchDynamicRouteConfig: flow(function* dynamicConfig(data: any) {
      try {
        const response = yield fetchDynamicRouteConfig(RootStore.session.getTokens, data)
        self.dynamicConfig = response
        self.unsavedDynamicConfig = response
        return response
      } catch (error) {
        self.dynamicConfig = undefined
        self.unsavedDynamicConfig = undefined
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    updateDynamicRouteConfig: flow(function* updateDynamicConfig() {
      try {
        const data = self.unsavedDynamicConfig.filter((dynamicConfig) => dynamicConfig?.editable)[0]
        const response = yield updateDynamicRouteConfig(RootStore.session.getTokens, data)
        RootStore.message.setMessage('Data saved Successfully', MessageTypes.SUCCESS)
        const savedData: any = JSON.parse(
          JSON.stringify(
            self.unsavedDynamicConfig.map((item) => {
              item.editable = false
              return item
            }),
          ),
        )
        self.unsavedDynamicConfig = savedData as any
        self.dynamicConfig = savedData as any
        return response
      } catch (error) {
        console.log(error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
    }),
    resetDynamicConfigList: flow(function* resetDynamicConfigList() {
      self.unsavedDynamicConfig = JSON.parse(JSON.stringify(self.dynamicConfig))
    }),
    clearDynamicConfigList: flow(function* clearDynamicConfigList() {
      self.unsavedDynamicConfig = []
      self.unsavedDynamicConfig = []
    }),
    getPaymentsInstrumentsList: flow(function* getPaymentsInstrumentsList(instrumentsType: number[]) {
      let response
      try {
        response = yield getPaymentsInstruments(instrumentsType[0], RootStore.session.getTokens)
        self.bankList = response as any
        response = yield getPaymentsInstruments(instrumentsType[1], RootStore.session.getTokens)
        self.walletList = response as any
        response = yield getCardSchemesListApi(RootStore.session.getTokens)
        self.cardList = response
        response = yield getEntityIdNameMap(RootStore.session.getTokens)
        self.entityIdNames = response
      } catch (error) {
        console.log('getPaymentsInstrumentsList error', error)
        RootStore.message.setMessage(getErrorMessage(error), MessageTypes.ERROR)
        throw error
      }
      return response
    }),
    toggleConfigListCheck: flow(function* toggleConfigListCheck(id: any) {
      console.log('data')
      self.unsavedDynamicConfig = JSON.parse(JSON.stringify(self.unsavedDynamicConfig)).map((data) => ({
        ...data,
        editable: data.id === id ? !data.editable : false,
      }))
    }),
    saveConfigDataChange: flow(function* saveConfigDataChange({ id, index, dataKey, value, data }: any) {
      self.unsavedDynamicConfig = JSON.parse(JSON.stringify(self.unsavedDynamicConfig)).map((data) => ({
        ...data,
        [dataKey]: data.id === id ? value : data[dataKey],
      }))
    }),
  }))
  .views((self) => ({
    getRoutingList() {
      const data = self.rules.map((item) => {
        return {
          id: '' + item?.id,
          ruleName: item?.ruleName,
          payeeType: item?.payeeType,
          payeeUserId: item?.payeeUserId,
          payeeVpa: item?.payeeVpa,
          payerBankId: item?.payerBankId,
          binList: item?.binList,
          binRange: item?.binRange,
          cardType: item?.cardType,
          scheme: item?.scheme,
          amount: item?.amount,
          transactionInitiatingEntityId: item?.transactionInitiatingEntityId,
          transactionApplication: item?.transactionApplication,
          txntype: item?.txntype,
          instrumentType: item?.instrumentType,
          mode: item?.mode,
          activationGroup: item?.activationGroup,
          priority: item?.priority,
          processor: item?.processor,
          isActive: item?.isActive,
          isAssigned: item?.isAssigned,
        }
      })
      return data
    },
    getRuleDetails(id: number) {
      const rule = { ...(self.rules.filter((item) => item.id === id)[0] ?? {}) }
      delete rule?.type
      delete rule?.isAssigned
      return {
        ...rule,
        cardType: getIdNameValue(rule.cardType, CardTypes),
        scheme: getIdNameValue(rule.scheme, CardSchema),
        txntype: getIdNameValue(rule.txntype, TransactionType),
        instrumentType: getIdNameValue(rule.instrumentType, InstrumentTypes),
        isActive: rule?.isActive === 'Y' ? 'Enabled' : 'Disabled',
      }
    },
    getPayerBankIds() {
      return self.payerBankIds.map((item, index) => ({
        id: index + 1,
        value: item,
      }))
    },
    getEntityData() {
      return self.entityIdNames.map((item) => ({
        id: item.id,
        value: item.name,
      }))
    },
    getRulesForFile() {
      return self.rules.map((item) => ({
        id: item?.id,
        value: `${item?.id}-${item?.ruleName}`,
      }))
    },
    getRulesForInitialValues(data: any) {
      try {
        type SectionType = {
          [key: string]: string
        }

        let res: SectionType = {}
        const arr = self.rules.map((item) => {
          res[`${item.id}`] = `${item.ruleName}`
          return item
        })

        const ins = data?.rules?.split(',') ?? []
        let result: any[] = []
        if (ins && ins.length > 0) {
          const some = ins.map((item: any) => {
            if (item !== '') {
              result.push(`${getIdNameValue(item, res)}`)
            }
            return item
          })
          console.log(some, arr)
        }

        return {
          fileName: data?.fileName ?? '',
          rules: result?.join(',') ?? {},
        }
      } catch (error) {
        console.log({ error })
      }
    },
    getDynamicConfigList() {
      console.log('in')
      return self.unsavedDynamicConfig
        .filter((item) => item.status === 'ACTIVE')
        .map((data: any) => ({
          id: data?.id,
          resetAfter: data?.resetAfter,
          plannedDowntimeStartAt: data?.plannedDowntimeStartAt,
          plannedDowntimeEndAt: data?.plannedDowntimeEndAt,
          lastTxnDate: data?.lastTxnDate,
          failureRate: data?.failureRate,
          editable: data?.editable,
          bankId: data?.bankId,
          processingEntity: data?.processingEntity,
        }))
    },
  }))

const routingRuleData = RoutingRule.create({
  rule: {},
  rules: [],
})

export type RoutingRuleType = Instance<typeof RoutingRule>
export type RuleType = Instance<typeof Rule>
export type DynamicConfig = Instance<typeof DynamicConfig>
export { DynamicConfig, RoutingRule, routingRuleData, Rule }
